import qc from 'qc'
import _ from 'lodash'

import {
  addAnimation,
  insertAnimation,
  removeAnimation,
  switchAnimation,
  updateAnimation,
} from '@ps/store/workbench/common/animation'
import { Event, EventBus } from '@ps/utils/event-bus'
import store from '@ps/store'
import { getVpAstFromSceneNode } from '@ps/bridge/qc/vp'
import { AstEventItem } from '@ps/bridge/qc/scripts/editor'
import { getActionsWithTweenChildrenAll } from '@ps/store/workbench/visual-program'

export type SetAnimationActionValue = {
  node: qc.Node
  newValue: {
    animations: qc.Behaviour[]
  }
  oldValue: {
    animations: qc.Behaviour[]
  }
}

export const getSetAnimationAction = (value: SetAnimationActionValue) => {
  return {
    value,
    execute: ({ node, newValue }: SetAnimationActionValue) => {
      const { animations } = newValue
      updateAnimation({ animations, node })
      EventBus.$emit(Event.ANIMATION_UPDATE)
    },
    undo: ({ node, oldValue }: SetAnimationActionValue) => {
      const { animations } = oldValue
      updateAnimation({ animations, node })
      EventBus.$emit(Event.ANIMATION_UPDATE)
    },
    methodName: 'getSetAnimationAction',
  }
}

export type AddAnimationActionValue = {
  newValue: {
    nodes: qc.Node[]
    animationScripts: {
      [key: string]: qc.Behaviour[]
    }
  }
  oldValue: {
    nodes: qc.Node[]
    animationScripts: {
      [key: string]: qc.Behaviour[]
    }
  }
}

/**
 * 为节点列表添加动画脚本
 * @param value
 */
export const getAddAnimationAction = (value: AddAnimationActionValue) => {
  return {
    value,
    execute: ({ newValue }: AddAnimationActionValue) => {
      const { animationScripts, nodes } = newValue
      _.each(nodes, (node) => {
        addAnimation({ animations: animationScripts[node.uuid], node })
      })
      EventBus.$emit(Event.ANIMATION_UPDATE)
    },
    undo: ({ oldValue }: AddAnimationActionValue) => {
      const { animationScripts, nodes } = oldValue
      _.each(nodes, (node) => {
        removeAnimation({ animations: animationScripts[node.uuid], node })
      })
      EventBus.$emit(Event.ANIMATION_UPDATE)
    },
    methodName: 'getAddAnimationAction',
  }
}

export type RewriteAnimationActionValue = {
  newValue: {
    nodes: qc.Node[]
    animationScripts: {
      [key: string]: qc.Behaviour[]
    }
    nodeAnimationScripts: {
      [key: string]: qc.Behaviour[]
    }
  }
  oldValue: {
    nodes: qc.Node[]
    animationScripts: {
      [key: string]: qc.Behaviour[]
    }
    nodeAnimationScripts: {
      [key: string]: qc.Behaviour[]
    }
  }
}

export const getRewriteAnimationAction = (
  value: RewriteAnimationActionValue,
) => {
  return {
    value,
    execute: ({ newValue }: RewriteAnimationActionValue) => {
      const { animationScripts, nodeAnimationScripts, nodes } = newValue
      _.each(nodes, (node) => {
        removeAnimation({ animations: nodeAnimationScripts[node.uuid], node })
      })
      _.each(nodes, (node) => {
        addAnimation({ animations: animationScripts[node.uuid], node })
      })
      EventBus.$emit(Event.ANIMATION_UPDATE)
    },
    undo: ({ oldValue }: RewriteAnimationActionValue) => {
      const { animationScripts, nodeAnimationScripts, nodes } = oldValue
      _.each(nodes, (node) => {
        removeAnimation({ animations: animationScripts[node.uuid], node })
      })
      _.each(nodes, (node) => {
        addAnimation({ animations: nodeAnimationScripts[node.uuid], node })
      })
      EventBus.$emit(Event.ANIMATION_UPDATE)
    },
    methodName: 'getRewriteAnimationAction',
  }
}

export type SwitchAnimationActionValue = {
  node: qc.Node
  newValue: {
    index: number
    animations: qc.Behaviour[]
    newAnimations: qc.Behaviour[]
  }
  oldValue: {
    index: number
    newAnimations: qc.Behaviour[]
    animations: qc.Behaviour[]
  }
}

export const getSwitchAnimationAction = (value: SwitchAnimationActionValue) => {
  return {
    value,
    execute: ({ node, newValue }: SwitchAnimationActionValue) => {
      const { animations, newAnimations, index } = newValue
      switchAnimation({ animations, newAnimations, index, node })
      EventBus.$emit(Event.ANIMATION_UPDATE)
    },
    undo: ({ node, oldValue }: SwitchAnimationActionValue) => {
      const { animations, newAnimations, index } = oldValue
      switchAnimation({
        animations: newAnimations,
        newAnimations: animations,
        index,
        node,
      })
      EventBus.$emit(Event.ANIMATION_UPDATE)
    },
    methodName: 'getSwitchAnimationAction',
  }
}

export type RemoveAnimationActionValue = {
  node: qc.Node
  newValue: {
    animations: qc.Behaviour[]
    actionsWithAnimation: Array<any>
    actionsWithVpPlayTweenAnimation: Array<any>
  }
  oldValue: {
    index: number
    animations: qc.Behaviour[]
  }
}

export const getRemoveAnimationAction = (value: RemoveAnimationActionValue) => {
  return {
    value,
    execute: ({ node, newValue }: RemoveAnimationActionValue) => {
      const {
        animations,
        actionsWithAnimation,
        actionsWithVpPlayTweenAnimation,
      } = newValue
      const sceneNode = store.state.workspace.game.sceneManager.currentSceneNode
      const sceneAst = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
      const oriAst = _.cloneDeep(sceneAst)
      removeAnimation({ animations, node })
      store.dispatch('workspace/game/visualProgram/removeActionsByAnimation', {
        actionsWithAnimation,
        actionsWithVpPlayTweenAnimation,
        node,
      })
      const actionsWithTweenChildrenAll = getActionsWithTweenChildrenAll()
      store.dispatch(
        'workspace/game/visualProgram/removeTweenChildrenAllActions',
        {
          actionsWithTweenChildrenAll,
        },
      )
      EventBus.$emit(Event.ANIMATION_UPDATE)
      return { oriAst }
    },
    undo: (
      { node, oldValue }: RemoveAnimationActionValue,
      response: { oriAst: AstEventItem[] },
    ) => {
      const { animations, index } = oldValue
      const sceneNode = store.state.workspace.game.sceneManager.currentSceneNode
      const vpAst = response.oriAst
      store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: vpAst,
        sceneNode,
      })
      insertAnimation({ animations, node, index })
      EventBus.$emit(Event.ANIMATION_UPDATE)
    },
    methodName: 'getRemoveAnimationAction',
  }
}
